import { Component, input, output, signal, computed, inject, effect } from '@angular/core';

import { NavItem, PlatformConfig } from '../../models/platform.model';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CustomButtonComponent } from '../../../shared/components/custom-button/custom-button.component';
import { filter } from 'rxjs';

@Component({
  selector: 'app-platform-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, FormsModule, CustomButtonComponent],
  templateUrl: './platform-sidebar.component.html',
  styleUrl: './platform-sidebar.component.css',
})
export class PlatformSidebar {
  config = input<PlatformConfig | null>(null);
  menuItems = input<NavItem[]>([]);
  isVisible = input<boolean>(false);
  isPinned = input<boolean>(false);
  sidebarClosed = output<void>();
  pin = output<void>();
  logout = output<void>();

  searchQuery = signal('');
  expandedItems = signal<Set<string>>(new Set());
  linkActiveOptions = computed(() => ({
    exact: false,
  }));

  private router = inject(Router);
  private currentUrl = signal(this.router.url);

  constructor() {
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.currentUrl.set(this.router.url);
      this.expandActiveSubmenus();
    });

    effect(() => {
      const items = this.menuItems();
      if (items.length > 0) {
        setTimeout(() => this.expandActiveSubmenus());
      }
    });

    effect(() => {
      if (this.isVisible()) {
        // Wait for the slide-in transition (0.3s) to complete
        setTimeout(() => this.scrollActiveIntoView(), 350);
      }
    });
  }

  private expandActiveSubmenus(): void {
    const currentUrl = this.router.url.split('?')[0];
    const newExpanded = new Set<string>();

    this.menuItems().forEach((item) => {
      if (item.children) {
        let itemHasActiveChild = false;

        item.children.forEach((child) => {
          const childMatches = this.isSidebarRouteActive(child.route, currentUrl);
          let grandchildMatches = false;

          if (child.children) {
            grandchildMatches = child.children.some((grandchild) =>
              this.isSidebarRouteActive(grandchild.route, currentUrl)
            );
          }

          if (childMatches || grandchildMatches) {
            itemHasActiveChild = true;
            if (grandchildMatches) {
              newExpanded.add(child.label);
            }
          }
        });

        if (itemHasActiveChild) {
          newExpanded.add(item.label);
        }
      }
    });

    const currentSet = this.expandedItems();
    let isDifferent = currentSet.size !== newExpanded.size;
    if (!isDifferent) {
      for (const val of newExpanded) {
        if (!currentSet.has(val)) {
          isDifferent = true;
          break;
        }
      }
    }

    if (isDifferent) {
      this.expandedItems.set(newExpanded);
    }

    this.scrollActiveIntoView();
  }

  private getActiveElement(): HTMLElement | null {
    const currentUrl = this.router.url.split('?')[0];
    
    // 1. Try finding by exact href attribute
    let element = document.querySelector(`.platform-sidebar a[href="${currentUrl}"]`) as HTMLElement;
    if (element) return element;
    
    // 2. Try finding by matching href (starts with or includes the url)
    const allLinks = document.querySelectorAll('.platform-sidebar a[href]');
    for (let i = 0; i < allLinks.length; i++) {
      const link = allLinks[i] as HTMLAnchorElement;
      const href = link.getAttribute('href');
      if (
        href &&
        (href === currentUrl || currentUrl.startsWith(href) || href.startsWith(currentUrl))
      ) {
        return link;
      }
    }
    
    // 3. Fallback to active class
    return document.querySelector('.platform-sidebar .active') as HTMLElement;
  }

  private scrollActiveIntoView(): void {
    const performScroll = () => {
      const activeElement = this.getActiveElement();
      const navContainer = document.querySelector('.sidebar-nav') as HTMLElement;

      if (activeElement && navContainer) {
        const activeRect = activeElement.getBoundingClientRect();
        const containerRect = navContainer.getBoundingClientRect();

        // Calculate vertical center of active element and the container relative to viewport
        const activeCenter = activeRect.top + activeRect.height / 2;
        const containerCenter = containerRect.top + containerRect.height / 2;
        const diff = activeCenter - containerCenter;

        navContainer.scrollBy({
          top: diff,
          behavior: 'smooth'
        });
      } else if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    };

    // We use a safe timeout to ensure Angular has completed change detection and DOM rendering
    setTimeout(() => {
      if (typeof document !== 'undefined' && document.readyState !== 'complete') {
        window.addEventListener('load', () => performScroll(), { once: true });
      } else {
        performScroll();
      }
    }, 300);
  }

  filteredMenuItems = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const items = this.menuItems();

    if (!query) return items;

    const filterItems = (navItems: NavItem[]): NavItem[] => {
      const filtered: NavItem[] = [];
      for (const item of navItems) {
        if (item.hideInSidebar) continue;

        const matchesLabel = item.label.toLowerCase().includes(query);
        let filteredChildren: NavItem[] | undefined;

        if (item.children) {
          filteredChildren = filterItems(item.children);
        }

        if (matchesLabel || (filteredChildren && filteredChildren.length > 0)) {
          filtered.push({
            ...item,
            children: matchesLabel ? item.children : filteredChildren,
          });
        }
      }
      return filtered;
    };

    return filterItems(items);
  });

  toggleSubmenu(label: string, event: Event, siblings: NavItem[] = []): void {
    event.preventDefault();
    event.stopPropagation();
    const current = new Set(this.expandedItems());
    if (current.has(label)) {
      current.delete(label);
    } else {
      // Accordion behavior: close siblings
      if (siblings.length > 0) {
        siblings.forEach((sibling) => {
          if (sibling.label !== label) {
            current.delete(sibling.label);
          }
        });
      }
      current.add(label);
    }
    this.expandedItems.set(current);
  }

  isExpanded(label: string): boolean {
    if (this.searchQuery()) {
      return true;
    }
    return this.expandedItems().has(label);
  }

  hasVisibleChildren(children?: NavItem[]): boolean {
    if (!children) return false;
    return children.some((c) => !c.hideInSidebar);
  }

  private isSidebarRouteActive(route: string | undefined, currentUrl: string): boolean {
    if (!route) return false;
    return currentUrl.startsWith(route);
  }

  isSvgIcon(icon: string): boolean {
    return icon.endsWith('.svg');
  }

  handleFooterClick(item: NavItem, event: Event): void {
    if (item.label === 'Logout') {
      event.preventDefault();
      this.logout.emit();
    }
    this.sidebarClosed.emit();
  }
}
